// M8 已切真后端（/api/articles）
// 图文分析：时间筛选 + 排序 + 搜索 + 表格
// 对齐 WeChat MP 「图文分析」:Segmented(近 7/30/90 天) + 搜索 + DateRangePicker,
// 列名:发布时间 / 标题 / 阅读 / 分享 / 评论 / 留言率
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input, Segmented } from 'antd'
import { Search } from 'lucide-react'
import PageHeader from '@/components/PageHeader'
import DateRangePicker from '@/components/DateRangePicker'
import DataTable from '@/components/DataTable'
import type { ColumnsType } from 'antd/es/table'
import { useArticleList, type ArticleListItem } from '@/mocks/articles'
import { formatNumber, formatPercent, formatDuration, timeAgo } from '@/lib/utils'

type Range = '7d' | '30d' | '90d'

const PAGE_SIZE_KEY = 'fenxi_articles_page_size'

function loadPageSize(): number {
  try {
    const v = parseInt(localStorage.getItem(PAGE_SIZE_KEY) || '', 10)
    if ([10, 100, 200].includes(v)) return v
  } catch { /* ignore */ }
  return 10
}

export default function ArticleList() {
  const navigate = useNavigate()
  const [range, setRange] = useState<Range>('30d')
  const [sort, setSort] = useState<'read' | 'time'>('time')
  const [search, setSearch] = useState('')
  const [pageSize, setPageSize] = useState(loadPageSize)

  const { data, isLoading } = useArticleList({ sort, search })

  const columns: ColumnsType<ArticleListItem> = [
    {
      title: '发布时间',
      dataIndex: 'publishTime',
      key: 'publishTime',
      width: 110,
      render: (v: number) => (
        <span title={new Date(v).toLocaleString('zh-CN')} className="text-ink-500 num text-[13px]">
          {timeAgo(v)}
        </span>
      ),
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 240,
      render: (t: string, r) => (
        <button
          type="button"
          onClick={() => navigate(`/articles/${r.id}`)}
          className="text-left text-ink-900 hover:text-link line-clamp-1 w-full"
          title={t}
        >
          {r.isOriginal && (
            <span className="inline-block mr-1 px-1 rounded-sm bg-warning/10 text-warning text-[11px]">
              原创
            </span>
          )}
          {t}
        </button>
      ),
    },
    {
      title: '阅读',
      dataIndex: 'readUser',
      key: 'readUser',
      width: 75,
      render: (v: number) => <span className="num text-ink-900 font-medium">{formatNumber(v)}</span>,
    },
    {
      title: '平均阅读',
      dataIndex: 'avgReadTime',
      key: 'avgReadTime',
      width: 80,
      render: (v: number) => <span className="num text-ink-700">{formatDuration(v)}</span>,
    },
    {
      title: '完读率',
      dataIndex: 'readFinishRate',
      key: 'readFinishRate',
      width: 70,
      render: (v: number) => <span className="num text-primary-600 font-medium">{formatPercent(v)}</span>,
    },
    {
      title: '听全文',
      dataIndex: 'audioPlayUser',
      key: 'audioPlayUser',
      width: 75,
      render: (v: number) => <span className="num text-ink-700">{formatNumber(v)}</span>,
    },
    {
      title: '分享',
      dataIndex: 'shareCount',
      key: 'shareCount',
      width: 65,
      render: (v: number) => <span className="num text-ink-700">{formatNumber(v)}</span>,
    },
    {
      title: '分享率',
      key: 'shareRate',
      width: 70,
      responsive: ['md'],
      render: (_v, r) => {
        const rate = r.readUser > 0 ? r.shareCount / r.readUser : 0
        return <span className="num text-ink-600">{formatPercent(rate)}</span>
      },
    },
    {
      title: '点赞',
      dataIndex: 'likeCount',
      key: 'likeCount',
      width: 60,
      render: (v: number) => <span className="num text-ink-700">{formatNumber(v)}</span>,
    },
    {
      title: '点赞率',
      key: 'likeRate',
      width: 70,
      responsive: ['md'],
      render: (_v, r) => {
        const rate = r.readUser > 0 ? r.likeCount / r.readUser : 0
        return <span className="num text-ink-600">{formatPercent(rate)}</span>
      },
    },
    {
      title: '推荐',
      dataIndex: 'recommendCount',
      key: 'recommendCount',
      width: 70,
      responsive: ['lg'],
      render: (v: number) => <span className="num text-ink-600">{formatNumber(v)}</span>,
    },
    {
      title: '留言',
      dataIndex: 'commentCount',
      key: 'commentCount',
      width: 60,
      responsive: ['lg'],
      render: (v: number) => <span className="num text-ink-600">{formatNumber(v)}</span>,
    },
  ]

  return (
    <div>
      <PageHeader
        title="图文分析"
        subtitle="所有抓取到的文章，支持多维筛选和排序"
        extra={
          <span className="text-sm text-ink-500 num">
            共 {data?.length ?? 0} 篇
          </span>
        }
      />

      {/* 工具栏 */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Segmented
          value={range}
          onChange={(v) => setRange(v as Range)}
          options={[
            { label: '近 7 天', value: '7d' },
            { label: '近 30 天', value: '30d' },
            { label: '近 90 天', value: '90d' },
          ]}
          className="shadow-sm"
        />
        <Segmented
          value={sort}
          onChange={(v) => setSort(v as typeof sort)}
          options={[
            { label: '按时间', value: 'time' },
            { label: '按阅读', value: 'read' },
          ]}
          className="shadow-sm"
        />
        <Input
          allowClear
          prefix={<Search size={14} className="text-ink-300" />}
          placeholder="搜索标题"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="!w-64"
        />
        <DateRangePicker className="ml-auto" />
      </div>

      {/* 表格 */}
      <DataTable
        columns={columns}
        dataSource={data ?? []}
        loading={isLoading}
        rowKey="id"
        size="small"
        scroll={{ x: 760 }}
        pagination={{
          defaultPageSize: pageSize,
          pageSizeOptions: [10, 100, 200],
          showSizeChanger: true,
          showTotal: (t: number) => `共 ${t} 条`,
          onShowSizeChange: (_current: number, size: number) => {
            setPageSize(size)
            localStorage.setItem(PAGE_SIZE_KEY, String(size))
          },
        }}
        onRow={(r) => ({
          onClick: () => navigate(`/articles/${r.id}`),
          style: { cursor: 'pointer' },
        })}
        className="card-interactive"
      />
    </div>
  )
}
